import type { CalcCostKind, CalcFile, CalculationStatus } from '../types/calculation.types'

/**
 * AP-37 4.1 — Anzeige und Farbe je Status: **eine** Stelle (Muster
 * `project-labels.ts`). Farben wie der Designvorschlag: in Arbeit blau,
 * abgegeben gelb, beauftragt grün, nicht beauftragt rot, verworfen grau.
 */
export const STATUS_ANZEIGE: Record<CalculationStatus, { label: string; klasse: string }> = {
  IN_PROGRESS: { label: 'in Arbeit', klasse: 'bg-sky-500/15 text-sky-800 dark:text-sky-300' },
  SUBMITTED: { label: 'abgegeben', klasse: 'bg-amber-100 text-amber-900 dark:bg-amber-500/20 dark:text-amber-300' },
  WON: { label: 'beauftragt', klasse: 'bg-green-100 text-green-900 dark:bg-green-500/20 dark:text-green-300' },
  LOST: { label: 'nicht beauftragt', klasse: 'bg-red-100 text-red-900 dark:bg-red-500/20 dark:text-red-300' },
  DISCARDED: { label: 'verworfen', klasse: 'bg-muted text-muted-foreground' },
}

export const DATEIART_ANZEIGE: Record<CalcFile['kind'], string> = {
  SUPPLIER_OFFER: 'Lieferantenangebot',
  MATERIAL_LIST: 'Stück-/Materialliste',
  SPECIFICATION: 'Leistungsbeschreibung, Anfrage',
  DRAWING: 'Plan, Zeichnung',
  OTHER: 'Sonstiges',
  OUTPUT: 'Ausgabe (gesperrt)',
}

/** Dateiarten zum Wählen — eine Ausgabe entsteht nur beim Ausgeben (K 7.2). */
export const WAEHLBARE_DATEIARTEN = (Object.keys(DATEIART_ANZEIGE) as (keyof typeof DATEIART_ANZEIGE)[]).filter((a) => a !== 'OUTPUT')

/** `02.10.2026` bzw. `02.10.2026 10:00` — in der Zeitzone des Browsers. */
export function datumText(iso: string | null | undefined, mitZeit = false): string {
  if (!iso) return ''
  const d = new Date(iso)
  const t = `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.${d.getFullYear()}`
  if (!mitZeit) return t
  const z = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
  return z === '00:00' ? t : `${t} ${z}`
}

/** Reines Datum (`@db.Date`) ohne Verschiebung durch die Zeitzone. */
export function tagText(iso: string | null | undefined): string {
  if (!iso) return ''
  const s = iso.slice(0, 10)
  return `${s.slice(8, 10)}.${s.slice(5, 7)}.${s.slice(0, 4)}`
}

/** Abgabetermin in höchstens drei Tagen — rot in der Liste (9.). */
export function abgabeDringend(iso: string | null | undefined): boolean {
  if (!iso) return false
  return new Date(iso).getTime() - Date.now() < 3 * 24 * 3600 * 1000
}

/** Stunden mit einer Nachkommastelle — Stunden sind kein Geld (`geldbetraege.md`). */
export function stundenText(h: number | null | undefined): string {
  if (h == null || !Number.isFinite(h)) return ''
  return h.toLocaleString('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 1 })
}

/**
 * Gewicht einer **Summe** (Nachtrag 7, N7-1): höchstens eine Nachkommastelle, ab 100 kg ohne — „12,4 kg“,
 * „446 kg“. Das Gewicht einer einzelnen Zeile und „kg je Einheit“ behalten ihre Stellen.
 */
export function gewichtText(kg: number | null | undefined): string {
  if (kg == null || !Number.isFinite(kg)) return ''
  // Die Grenze gilt für den auf eine Stelle gerundeten Wert: 99,96 kg sind „100 kg“, nicht „100,0 kg“.
  // Formatiert wird der ungerundete — zweimal gerundet würden aus 123,46 kg erst 123,5 und dann 124.
  const stellen = Math.abs(Math.round(kg * 10) / 10) >= 100 ? 0 : 1
  return `${kg.toLocaleString('de-DE', { maximumFractionDigits: stellen })} kg`
}

export function prozentText(p: number | null | undefined, stellen = 1): string {
  if (p == null || !Number.isFinite(p)) return ''
  return `${p.toLocaleString('de-DE', { minimumFractionDigits: stellen, maximumFractionDigits: stellen })} %`
}

/** Der Kunde einer Zeile — Stamm oder Interessent. */
export function kundeText(z: { client: { name: string } | null; prospectName: string | null; prospectCity: string | null }): string {
  if (z.client) return z.client.name
  if (z.prospectName) return `Interessent: ${[z.prospectName, z.prospectCity].filter(Boolean).join(', ')}`
  return ''
}

/** Kostenarten (4.3, 4.7) — Reihenfolge wie im Rechenkern. */
export const KOSTENARTEN: CalcCostKind[] = ['LABOR', 'MATERIAL', 'SUBCONTRACT', 'EQUIPMENT', 'OTHER']
export const KOSTENART_TEXT: Record<CalcCostKind, string> = {
  LABOR: 'Lohn',
  MATERIAL: 'Material',
  SUBCONTRACT: 'Fremdleistung',
  EQUIPMENT: 'Gerät',
  OTHER: 'Sonstiges',
}
export const KOSTENART_KURZ: Record<CalcCostKind, string> = { LABOR: 'L', MATERIAL: 'M', SUBCONTRACT: 'F', EQUIPMENT: 'G', OTHER: 'S' }
